import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import {
  ChevronRight, ChevronLeft, FileText, Download, Check, Plus, AlertTriangle,
  Loader2, LayoutTemplate, ClipboardList, SearchCheck, FileCheck2, UploadCloud,
  Pencil, CircleAlert, CircleCheck, CircleHelp, Maximize2, ZoomIn, ZoomOut,
  FileSpreadsheet, FileType2, Eye, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";
import { toast } from "sonner";
import A4Preview from "@/components/negotiation/A4Preview";
import { fetchSports } from "@/lib/sportsCatalog";
import { translateFr as tfr } from "@/i18n";

const STEPS = [
  { id: "template", n: 1, label: "Choix du template", Icon: LayoutTemplate },
  { id: "import", n: 2, label: tfr("Importer les données"), Icon: ClipboardList },
  { id: "review", n: 3, label: tfr("Vérification"), Icon: SearchCheck },
  { id: "generate", n: 4, label: tfr("Génération PDF"), Icon: FileCheck2 },
];

const EMPTY_FORM = {
  market_value: "", salary_expectation: "", transfer_fee: "", contract_end_date: "",
  availability: "", preferred_market: "", strengths: "", notes: "", season_label: "", season_stats: {},
};
const DEFAULT_SECTIONS = { profile: true, contract: true, market_value: true, career: true, agent_contact: true, qr_code: true };

const STATUS_META = {
  recognised: { label: "Reconnu", cls: "text-success bg-success/10 border-success/30", Icon: CircleCheck },
  needs_review: { label: tfr("À vérifier"), cls: "text-warning bg-warning/10 border-warning/30", Icon: CircleAlert },
  conflict: { label: "Conflit", cls: "text-warning bg-warning/10 border-warning/30", Icon: CircleHelp },
  manual: { label: tfr("Modifié manuellement"), cls: "text-info bg-info/10 border-info/30", Icon: Pencil },
  missing: { label: tfr("Non détecté"), cls: "text-amber-500/90 bg-amber-500/[0.08] border-amber-500/25", Icon: AlertTriangle },
  prefilled: { label: "Profil Athlynx", cls: "text-muted-foreground bg-secondary/50 border-border", Icon: CircleCheck },
};

const showPaywall = () =>
  window.dispatchEvent(new CustomEvent("athlynx:paywall", {
    detail: { message: tfr("Votre essai est expiré. Réactivez votre abonnement pour générer et enregistrer la fiche.") },
  }));

export default function NegotiationProfile({ listView = false }) {
  const { id, step } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [player, setPlayer] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [me, setMe] = useState({});
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [templateId, setTemplateId] = useState("standard");
  const [form, setForm] = useState(EMPTY_FORM);
  const [identity, setIdentity] = useState({});
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [fieldMeta, setFieldMeta] = useState({});
  const [importInfo, setImportInfo] = useState({ state: "idle", progress: 0, file: null, id: null });
  const [expanded, setExpanded] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const go = useCallback((s) => navigate(`/players/${id}/negotiation-profile/${s}`), [navigate, id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [pr, rd, pf, meR, sp] = await Promise.all([
          api.get(`/players/${id}`),
          api.get(`/players/${id}/negotiation-profile/readiness`),
          api.get(`/players/${id}/negotiation-profiles`),
          api.get(`/auth/me`),
          fetchSports(),
        ]);
        if (!alive) return;
        setPlayer(pr.data.player);
        setReadiness(rd.data);
        setProfiles(pf.data || []);
        setMe(meR.data || {});
        setSports(sp || []);
        if (!listView && !step) {
          navigate(
            (pf.data || []).length > 0
              ? `/players/${id}/negotiation-profiles`
              : `/players/${id}/negotiation-profile/template`,
            { replace: true }
          );
        }
      } catch (e) {
        toast.error(e?.response?.status === 404 ? tfr("Le profil de l'athlète est introuvable.") : tfr("Impossible de préparer la fiche de négociation."));
        navigate("/pipeline", { replace: true });
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Prefill when arriving from "Mettre à jour la dernière fiche"
  useEffect(() => {
    const pre = location.state?.prefill;
    if (pre) {
      setTemplateId(pre.template_id || "standard");
      const d = pre.data || {};
      setForm({ ...EMPTY_FORM, ...d, season_stats: { ...(d.season_stats || {}) } });
      setIdentity({ ...(d.identity || {}) });
      setSections({ ...DEFAULT_SECTIONS, ...(d.sections || {}) });
    }
  }, [location.state]);

  const sportLabel = useMemo(
    () => sports.find((s) => s.id === (player?.sport || "football"))?.label || "Football",
    [sports, player]
  );
  const fieldDefs = useMemo(() => readiness?.field_defs || [], [readiness]);
  const statFields = readiness?.stats_fields || [];

  const getValue = useCallback((def) => {
    if (def.group === "stats") return form.season_stats[def.id] ?? "";
    if (def.group === "contract") return form[def.id] ?? "";
    return identity[def.id] ?? player?.[def.id] ?? player?.sport_data?.[def.id] ?? "";
  }, [form, identity, player]);

  const setValue = useCallback((def, v, status = "manual") => {
    if (def.group === "stats") setForm((f) => ({ ...f, season_stats: { ...f.season_stats, [def.id]: v } }));
    else if (def.group === "contract") setForm((f) => ({ ...f, [def.id]: v }));
    else setIdentity((o) => ({ ...o, [def.id]: v }));
    setFieldMeta((m) => ({ ...m, [def.id]: { ...(m[def.id] || {}), status } }));
  }, []);

  const statusOf = useCallback((def) => {
    const meta = fieldMeta[def.id];
    if (meta?.status) return meta.status;
    const v = getValue(def);
    if (v !== "" && v !== null && v !== undefined) return "prefilled";
    return "missing";
  }, [fieldMeta, getValue]);

  const applyImport = useCallback((resp) => {
    const meta = {};
    resp.detected_fields.forEach((f) => {
      meta[f.field_id] = { status: f.status, confidence: f.confidence, source: f.source, existing: f.existing, imported: f.value };
      if (f.status !== "conflict") {
        const def = fieldDefs.find((d) => d.id === f.field_id);
        if (def) {
          if (def.group === "stats") setForm((fm) => ({ ...fm, season_stats: { ...fm.season_stats, [def.id]: f.value } }));
          else if (def.group === "contract") setForm((fm) => ({ ...fm, [def.id]: f.value }));
          else setIdentity((o) => ({ ...o, [def.id]: f.value }));
        }
      }
    });
    setFieldMeta((m) => ({ ...m, ...meta }));
    setForm((f) => ({ ...f, import_id: resp.import_id }));
  }, [fieldDefs]);

  const upload = useCallback(async (file) => {
    if (!file || importInfo.state === "uploading" || importInfo.state === "processing") return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "pdf"].includes(ext)) {
      toast.error(tfr("Format non supporté. Utilisez un fichier CSV, XLSX ou PDF."));
      return;
    }
    if (file.size > (readiness?.max_import_size || 8 * 1024 * 1024)) {
      toast.error("Fichier trop volumineux (max 8 Mo).");
      return;
    }
    setImportInfo({ state: "uploading", progress: 0, file: file.name, id: null });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/players/${id}/negotiation-profile/import`, fd, {
        onUploadProgress: (e) => {
          const p = e.total ? Math.round((e.loaded / e.total) * 100) : 50;
          setImportInfo((s) => ({ ...s, state: p >= 100 ? "processing" : "uploading", progress: p }));
        },
      });
      applyImport(r.data);
      const n = r.data.detected_fields.length;
      setImportInfo({ state: n ? "done" : "partial", progress: 100, file: r.data.file.name, id: r.data.import_id, detected: n });
      if (n) toast.success(`${n} ${n > 1 ? tfr("champs détectés") : tfr("champ détecté")} ${tfr("dans le fichier")}`);
      else toast.warning(tfr("Aucun champ reconnu dans ce fichier."));
    } catch (e) {
      setImportInfo({ state: "failed", progress: 0, file: file.name, id: null });
      toast.error(e?.response?.data?.detail || "Impossible d'analyser ce fichier.");
    }
  }, [id, importInfo.state, readiness, applyImport]);

  const generate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const data = { ...form, identity, sections };
      const created = await api.post(`/players/${id}/negotiation-profiles`, { template_id: templateId, data });
      const gen = await api.post(`/players/${id}/negotiation-profiles/${created.data.id}/generate`, { lang: localStorage.getItem("athlynx_lang") === "en" ? "en" : "fr" });
      setResult(gen.data);
      toast.success(tfr("Fiche de négociation générée"));
    } catch (e) {
      if (e?.response?.status === 402) showPaywall();
      else toast.error(tfr("Impossible de générer la fiche de négociation."));
    } finally { setGenerating(false); }
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const r = await api.get(`/players/${id}/documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "fiche-negociation.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(tfr("Téléchargement impossible.")); }
  };

  if (loading || !player || !readiness) {
    return <div className="p-8 text-sm text-muted-foreground" data-testid="negotiation-loading">{tfr("Chargement…")}</div>;
  }

  const currentStep = STEPS.find((s) => s.id === step) || STEPS[0];
  const stepLabel = listView ? "Fiches existantes" : currentStep.label;
  const previewProps = { player, sportLabel, identity, form, statFields, agent: { name: me.name, email: me.email, role: "Agent" }, sections };
  const attention = fieldDefs.filter((d) => ["needs_review", "conflict"].includes(statusOf(d))).length;
  const recognised = fieldDefs.filter((d) => ["recognised"].includes(statusOf(d))).length;
  const missingDefs = fieldDefs.filter((d) => statusOf(d) === "missing");

  return (
    <div className="px-4 md:px-8 py-6 max-w-[1500px] mx-auto pb-6" data-testid="negotiation-page">
      {/* Breadcrumb */}
      <nav aria-label="Fil d'Ariane" className="flex items-center flex-wrap gap-1 text-xs text-muted-foreground" data-testid="negotiation-breadcrumb">
        <Link to="/pipeline" className="hover:text-foreground transition-colors">{tfr("Athlètes")}</Link>
        <span className="sr-only"> / </span>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <Link to={`/players/${id}`} className="hover:text-foreground transition-colors">{player.name}</Link>
        <span className="sr-only"> / </span>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className="text-foreground/80">{tfr("Fiche de négociation")}</span>
        <span className="sr-only"> / </span>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className="text-foreground">{stepLabel}</span>
      </nav>

      {!listView && (
        <div className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/10 px-3.5 py-2.5 mt-4 text-xs text-foreground/80"
          data-testid="advanced-edit-warning">
          <span className="font-semibold text-warning shrink-0">{tfr("Édition avancée")}</span>
          <span>{tfr("L'édition avancée modifie directement les données utilisées pour le document.")}</span>
          <Link to={`/players/${id}/negotiation-profile`} className="ml-auto shrink-0 underline hover:text-athlynx" data-testid="back-to-guided-link">
            {tfr("Revenir à l'éditeur guidé")}
          </Link>
        </div>
      )}

      {!listView && (
        <ol className="flex items-center gap-1 mt-4 overflow-x-auto scrollbar-hide" data-testid="negotiation-stepper">
          {STEPS.map((s, i) => {
            const state = s.n === currentStep.n ? "current" : s.n < currentStep.n ? "done" : "todo";
            return (
              <li key={s.id} className="flex items-center gap-1 shrink-0">
                <div
                  className={cn(
                    "flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium border",
                    state === "current" && "bg-primary/15 text-primary border-primary/40",
                    state === "done" && "text-success border-success/30 bg-success/[0.06]",
                    state === "todo" && "text-muted-foreground border-border"
                  )}
                  aria-current={state === "current" ? "step" : undefined}
                >
                  {state === "done" ? <Check className="h-3 w-3" /> : <s.Icon className="h-3 w-3" />}
                  <span className="whitespace-nowrap">{s.n}. {s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={cn("w-6 h-px", state === "done" ? "bg-success/40" : "bg-secondary/60")} />}
              </li>
            );
          })}
        </ol>
      )}

      {listView && (
        <LandingList profiles={profiles} readiness={readiness} playerId={id}
          onDownload={downloadDoc}
          onNew={() => go("template")}
          onUpdateLast={() => navigate(`/players/${id}/negotiation-profile/template`, { state: { prefill: profiles[0] } })} />
      )}

      {!listView && step === "template" && (
        <div className="mt-6 space-y-4 max-w-3xl" data-testid="negotiation-step-template">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {readiness.templates.map((t) => (
              <button key={t.id} type="button" onClick={() => setTemplateId(t.id)}
                className={cn("rounded-2xl border p-5 text-left transition-all",
                  templateId === t.id ? "border-primary/60 bg-primary/[0.06] ring-2 ring-primary/25" : "border-border bg-surface hover:border-foreground/25")}
                data-testid={`negotiation-template-${t.id}`} aria-pressed={templateId === t.id}>
                <div className="flex items-center justify-between">
                  <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><LayoutTemplate className="h-4 w-4" /></div>
                  {templateId === t.id && <Check className="h-4 w-4 text-primary" />}
                </div>
                <div className="text-sm font-semibold mt-3">{t.label}</div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.description}</p>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" className="h-10 text-muted-foreground" onClick={() => navigate(`/players/${id}`)}>{tfr("Annuler")}</Button>
            <Button onClick={() => go("import")} className="h-10 gap-1.5" data-testid="negotiation-step1-next">
              Continuer <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {!listView && step === "import" && (
        <ImportStep
          player={player} readiness={readiness} fieldDefs={fieldDefs} importInfo={importInfo}
          upload={upload} getValue={getValue} setValue={setValue} statusOf={statusOf} fieldMeta={fieldMeta}
          setFieldMeta={setFieldMeta} setIdentity={setIdentity}
          previewProps={previewProps} missingDefs={missingDefs} recognised={recognised} attention={attention}
          onExpand={() => setExpanded(true)} onComplete={() => setCompleteOpen(true)}
          onCancel={() => navigate(`/players/${id}`)} onBack={() => go("template")} onContinue={() => go("review")}
        />
      )}

      {!listView && step === "review" && (
        <ReviewStep form={form} identity={identity} sections={sections} setSections={setSections}
          setForm={setForm} readiness={readiness} templateId={templateId} setTemplateId={setTemplateId}
          previewProps={previewProps} attention={attention}
          onBack={() => go("import")} onNext={() => go("generate")} onExpand={() => setExpanded(true)} />
      )}

      {!listView && step === "generate" && (
        <div className="mt-6 max-w-2xl" data-testid="negotiation-step-generate">
          <div className="rounded-2xl border border-border bg-surface p-6 text-center py-10">
            {result ? (
              <>
                <div className="h-12 w-12 rounded-full bg-success/15 text-success flex items-center justify-center mx-auto"><Check className="h-6 w-6" /></div>
                <h3 className="text-base font-semibold mt-4">{tfr("Fiche générée avec succès")}</h3>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {result.filename} — enregistrée dans les Documents de {player.name}.
                </p>
                <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
                  <Button onClick={() => downloadDoc(result.document_id, result.filename)} className="h-11 gap-1.5" data-testid="negotiation-download-result">
                    <Download className="h-4 w-4" /> Télécharger le PDF
                  </Button>
                  <Button variant="outline" className="h-11 border-border" onClick={() => navigate(`/players/${id}`)} data-testid="negotiation-back-profile">{tfr("Retour au profil")}</Button>
                </div>
              </>
            ) : (
              <>
                <div className="h-12 w-12 rounded-full bg-primary/15 text-primary flex items-center justify-center mx-auto"><FileCheck2 className="h-6 w-6" /></div>
                <h3 className="text-base font-semibold mt-4">{tfr("Générer la fiche de négociation")}</h3>
                <p className="text-xs text-muted-foreground mt-1.5 max-w-sm mx-auto">
                  {tfr("Le PDF A4 sera créé avec les données vérifiées puis enregistré comme nouvelle version dans les Documents de l'athlète.")}
                </p>
                <div className="flex items-center justify-center gap-2 mt-5">
                  <Button variant="outline" size="sm" className="h-11 border-border" onClick={() => go("review")} disabled={generating}>
                    <ChevronLeft className="h-4 w-4 mr-1" /> Retour
                  </Button>
                  <Button onClick={generate} disabled={generating} aria-busy={generating} className="h-11 gap-1.5 min-w-[190px]" data-testid="negotiation-generate-btn">
                    {generating ? (<><Loader2 className="h-4 w-4 animate-spin" /> {tfr("Génération…")}</>) : (<>{tfr("Générer le PDF")}</>)}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Expanded preview */}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <ExpandedPreview previewProps={previewProps} />
      </Dialog>

      {/* Complete manually */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="negotiation-complete-dialog">
          <DialogHeader><DialogTitle>{tfr("Compléter manuellement")}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">{tfr("Seuls les champs non résolus sont affichés.")}</p>
          <div className="space-y-3 mt-1">
            {missingDefs.length === 0 && <p className="text-sm text-success">{tfr("Tous les champs sont renseignés.")}</p>}
            {missingDefs.map((d) => (
              <div key={d.id} className="space-y-1">
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground/80" htmlFor={`complete-${d.id}`}>{d.label}</label>
                <Input id={`complete-${d.id}`} type={d.type === "date" ? "date" : "text"} value={getValue(d)}
                  onChange={(e) => setValue(d, e.target.value)} className="h-11 border-border bg-background/60"
                  data-testid={`negotiation-complete-${d.id}`} />
              </div>
            ))}
          </div>
          <Button className="w-full h-11 mt-2" onClick={() => setCompleteOpen(false)}>{tfr("Terminé")}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Import step (2-column layout + sticky footer) ---------- */
function ImportStep({
  player, readiness, fieldDefs, importInfo, upload, getValue, setValue, statusOf, fieldMeta, setFieldMeta,
  setIdentity, previewProps, missingDefs, recognised, attention, onExpand, onComplete, onCancel, onBack, onContinue,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const busy = ["uploading", "processing"].includes(importInfo.state);

  const resolveConflict = (def, useImported) => {
    const meta = fieldMeta[def.id] || {};
    if (useImported) setIdentity((o) => ({ ...o, [def.id]: meta.imported }));
    else setIdentity((o) => ({ ...o, [def.id]: meta.existing }));
    setFieldMeta((m) => ({ ...m, [def.id]: { ...meta, status: useImported ? "recognised" : "prefilled" } }));
  };

  const pick = (accept) => {
    if (inputRef.current) {
      inputRef.current.accept = accept;
      inputRef.current.click();
    }
  };

  return (
    <>
      <div className="mt-5">
        <h1 className="text-xl md:text-2xl font-semibold tracking-tight" data-testid="negotiation-import-title">
          {tfr("Importer les statistiques pour la fiche de négociation")}
        </h1>
        <p className="text-xs md:text-sm text-muted-foreground mt-1.5 max-w-2xl leading-relaxed">
          Importez un fichier contenant les statistiques de l'athlète. Athlynx détecte et remplit automatiquement
          les champs du template. Vous pourrez vérifier et modifier chaque donnée avant la génération du PDF.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] gap-5 items-start mt-5">
        {/* LEFT column */}
        <div className="space-y-4 min-w-0">
          {/* Upload card */}
          <div className="rounded-2xl border border-border bg-surface p-5" data-testid="negotiation-upload-card">
            <h2 className="text-sm font-semibold">1. Importer un fichier de statistiques</h2>
            <div
              role="button" tabIndex={0} aria-label={tfr("Zone d'import de fichier — cliquez ou déposez un fichier")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") pick(".csv,.xlsx,.pdf"); }}
              onClick={() => !busy && pick(".csv,.xlsx,.pdf")}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!busy) upload(e.dataTransfer.files?.[0]); }}
              className={cn(
                "mt-3 rounded-xl border border-dashed px-5 py-8 text-center transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                dragOver ? "border-primary/60 bg-primary/[0.05]" : "border-border bg-background/40 hover:border-foreground/25"
              )}
              data-testid="negotiation-upload-zone"
              aria-busy={busy}
            >
              <input ref={inputRef} type="file" className="sr-only" accept=".csv,.xlsx,.pdf"
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="negotiation-file-input" aria-label={tfr("Choisir un fichier de statistiques")} />
              {busy ? (
                <div className="space-y-2" data-testid="negotiation-upload-progress">
                  <Loader2 className="h-7 w-7 mx-auto animate-spin text-primary" />
                  <div className="text-sm font-medium">{importInfo.state === "processing" ? "Analyse du fichier…" : `Envoi… ${importInfo.progress}%`}</div>
                  <div className="text-xs text-muted-foreground truncate">{importInfo.file}</div>
                  <div className="h-1.5 w-48 mx-auto rounded-full bg-secondary/50 overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${importInfo.progress}%` }} />
                  </div>
                </div>
              ) : (
                <>
                  <UploadCloud className="h-7 w-7 mx-auto text-muted-foreground" />
                  <div className="text-sm font-medium mt-2">{tfr("Glissez-déposez votre fichier ici")}</div>
                  <div className="text-[11px] text-muted-foreground my-1.5">{tfr("ou")}</div>
                  <span className="inline-flex items-center h-9 px-4 rounded-lg border border-primary/60 text-primary text-xs font-semibold">
                    {tfr("Parcourir les fichiers")}
                  </span>
                  <div className="text-[11px] text-muted-foreground mt-3">{tfr("Formats supportés : CSV, XLSX, PDF — max 8 Mo")}</div>
                  {importInfo.state === "done" && (
                    <div className="text-[11px] text-success mt-2 inline-flex items-center gap-1" data-testid="negotiation-import-success">
                      <CircleCheck className="h-3.5 w-3.5" /> {importInfo.file} analysé — {importInfo.detected} champs détectés. Déposez un autre fichier pour remplacer.
                    </div>
                  )}
                  {importInfo.state === "failed" && (
                    <div className="text-[11px] text-destructive mt-2 inline-flex items-center gap-1"><CircleAlert className="h-3.5 w-3.5" /> Échec de l'analyse de {importInfo.file}.</div>
                  )}
                </>
              )}
            </div>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 text-center">{tfr("Ou importer depuis une source rapide")}</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {[
                  { id: "statsbomb", label: "StatsBomb CSV", Icon: FileSpreadsheet, accept: ".csv" },
                  { id: "wyscout", label: "Wyscout export", Icon: FileSpreadsheet, accept: ".csv,.xlsx" },
                  { id: "excel", label: "Excel manuel", Icon: FileSpreadsheet, accept: ".xlsx" },
                  { id: "pdf", label: "Capture PDF", Icon: FileType2, accept: ".pdf" },
                ].map((s) => (
                  <button key={s.id} type="button" onClick={() => !busy && pick(s.accept)}
                    className="h-11 rounded-lg border border-border bg-background/40 hover:border-foreground/25 text-xs font-medium inline-flex items-center justify-center gap-1.5 transition-colors"
                    data-testid={`negotiation-quick-${s.id}`}>
                    <s.Icon className="h-3.5 w-3.5 text-muted-foreground" /> {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Detected fields card */}
          <div className="rounded-2xl border border-border bg-surface p-5" data-testid="negotiation-detected-card">
            <h2 className="text-sm font-semibold">{tfr("2. Champs détectés")}</h2>
            {/* Desktop table */}
            <div className="hidden sm:block mt-3 overflow-x-auto">
              <table className="w-full text-sm" data-testid="negotiation-detected-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-muted-foreground/70 text-left border-b border-border">
                    <th className="py-2 pr-3 font-medium">{tfr("Champ du template")}</th>
                    <th className="py-2 pr-3 font-medium">{tfr("Valeur détectée")}</th>
                    <th className="py-2 pr-3 font-medium">{tfr("Statut")}</th>
                    <th className="py-2 font-medium sr-only">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {fieldDefs.map((d) => (
                    <FieldRow key={d.id} def={d} value={getValue(d)} status={statusOf(d)} meta={fieldMeta[d.id]}
                      onChange={(v) => setValue(d, v)} onResolve={(useImp) => resolveConflict(d, useImp)} />
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="sm:hidden mt-3 space-y-2.5" data-testid="negotiation-detected-mobile">
              {fieldDefs.map((d) => {
                const st = statusOf(d);
                const M = STATUS_META[st];
                return (
                  <div key={d.id} className="rounded-xl border border-border bg-background/40 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium">{d.label}</span>
                      <Badge className={cn("text-[9px] border gap-1", M.cls)}><M.Icon className="h-2.5 w-2.5" />{M.label}</Badge>
                    </div>
                    <Input type={d.type === "date" ? "date" : "text"} value={getValue(d)} onChange={(e) => setValue(d, e.target.value)}
                      className="h-11 mt-2 border-border bg-background/60" aria-label={d.label} />
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-3 border-t border-border flex items-center gap-4 flex-wrap text-[11px]" data-testid="negotiation-readiness-summary">
              <span className="inline-flex items-center gap-1.5 text-success"><CircleCheck className="h-3.5 w-3.5" /> {recognised} {recognised > 1 ? tfr("champs reconnus avec succès") : tfr("champ reconnu avec succès")}</span>
              <span className={cn("inline-flex items-center gap-1.5", attention ? "text-warning" : "text-muted-foreground")}>
                <CircleAlert className="h-3.5 w-3.5" /> {attention} {attention > 1 ? tfr("champs nécessitent votre attention") : tfr("champ nécessite votre attention")}
              </span>
              {importInfo.state === "idle" && <span className="text-muted-foreground">{tfr("Aucun champ reconnu pour le moment. Importez un fichier pour commencer.")}</span>}
            </div>
          </div>
        </div>

        {/* RIGHT column: preview + missing data */}
        <div className="space-y-4 min-w-0 xl:sticky xl:top-4">
          <div className="rounded-2xl border border-border bg-surface p-4" data-testid="negotiation-preview-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">{tfr("Aperçu du template")}</h2>
              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs border-border" onClick={onExpand} data-testid="negotiation-preview-expand">
                <Maximize2 className="h-3.5 w-3.5" /> Agrandir l'aperçu
              </Button>
            </div>
            <A4Preview {...previewProps} />
          </div>
          {missingDefs.length > 0 && (
            <div className="rounded-2xl border border-warning/20 bg-surface p-5" data-testid="negotiation-missing-card">
              <h2 className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-warning" /> {tfr("Données manquantes")}</h2>
              <ul className="mt-3 space-y-1.5">
                {missingDefs.slice(0, 8).map((d) => (
                  <li key={d.id} className="flex items-center justify-between text-xs">
                    <span>{d.label}</span>
                    <span className="text-amber-500/80 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {tfr("Non détecté")}</span>
                  </li>
                ))}
                {missingDefs.length > 8 && <li className="text-[11px] text-muted-foreground">+ {missingDefs.length - 8} autres champs…</li>}
              </ul>
              <Button variant="outline" size="sm" className="h-10 w-full mt-3 border-border" onClick={onComplete} data-testid="negotiation-complete-manually">
                {tfr("Compléter manuellement")}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 z-20 -mx-4 md:-mx-8 mt-6 border-t border-border bg-[rgba(8,13,21,0.96)] backdrop-blur-xl px-4 md:px-8 py-3.5"
        data-testid="negotiation-workflow-footer">
        <div className="max-w-[1500px] mx-auto flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <p className="text-[11px] text-muted-foreground">{tfr("Vous pourrez modifier toutes les données détectées avant la génération du PDF.")}</p>
          <div className="grid grid-cols-1 sm:flex gap-2">
            <Button variant="ghost" className="h-11 text-muted-foreground" onClick={onCancel} data-testid="negotiation-footer-cancel">{tfr("Annuler")}</Button>
            <Button variant="outline" className="h-11 gap-1.5 border-border" onClick={onExpand} data-testid="negotiation-footer-preview">
              <Eye className="h-4 w-4" /> Prévisualiser
            </Button>
            <Button className="h-11 gap-1.5" onClick={onContinue} data-testid="negotiation-footer-continue">
              Continuer vers la vérification <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

function FieldRow({ def, value, status, meta, onChange, onResolve }) {
  const [editing, setEditing] = useState(false);
  const M = STATUS_META[status] || STATUS_META.missing;
  return (
    <tr data-testid={`negotiation-field-row-${def.id}`}>
      <td className="py-2 pr-3 text-xs font-medium whitespace-nowrap">{def.label}</td>
      <td className="py-2 pr-3 min-w-[130px]">
        {editing || status === "missing" ? (
          <Input autoFocus={editing} type={def.type === "date" ? "date" : "text"} value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setEditing(true)}
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape" || e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
            }}
            className="h-9 text-xs border-border bg-background/60" aria-label={def.label}
            placeholder="—" data-testid={`negotiation-field-input-${def.id}`} />
        ) : (
          <button type="button" className="text-sm text-left hover:text-primary transition-colors" onClick={() => setEditing(true)}
            title={meta?.source ? `${tfr("Détecté depuis :")} ${meta.source} · ${tfr("Confiance")} ${Math.round((meta.confidence || 0) * 100)}%` : tfr("Modifier")}>
            {value === "" || value === null ? "—" : String(value)}
          </button>
        )}
        {status === "conflict" && meta && (
          <div className="mt-1.5 text-[10px] text-muted-foreground space-x-2" data-testid={`negotiation-conflict-${def.id}`}>
            <span>Athlynx : <b className="text-foreground">{String(meta.existing)}</b> {tfr("· Importé :")} <b className="text-foreground">{String(meta.imported)}</b></span>
            <button className="text-primary underline" onClick={() => onResolve(false)}>Garder</button>
            <button className="text-primary underline" onClick={() => onResolve(true)}>Utiliser l'import</button>
          </div>
        )}
        {meta?.source && status !== "conflict" && (
          <div className="text-[9px] text-muted-foreground/70 mt-0.5">Colonne « {meta.source} » · {Math.round((meta.confidence || 0) * 100)}%</div>
        )}
      </td>
      <td className="py-2 pr-3">
        <Badge className={cn("text-[9px] border gap-1 whitespace-nowrap", M.cls)} data-testid={`negotiation-field-status-${def.id}`}>
          <M.Icon className="h-2.5 w-2.5" /> {M.label}
        </Badge>
      </td>
      <td className="py-2 text-right">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setEditing((e) => !e)} aria-label={`${tfr("Modifier")} ${def.label}`}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60">
          {editing ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
        </button>
      </td>
    </tr>
  );
}
export { FieldRow };

/* ---------- Review step ---------- */
function ReviewStep({ form, identity, sections, setSections, setForm, readiness, templateId, setTemplateId, previewProps, attention, onBack, onNext, onExpand }) {
  const toggles = [
    ["contract", tfr("Section contrat & marché")],
    ["market_value", tfr("Afficher la valeur marchande et le salaire")],
    ["profile", tfr("Profil athlète (points forts)")],
    ["career", tfr("Carrière & faits marquants")],
    ["agent_contact", tfr("Coordonnées de l'agent")],
    ["qr_code", tfr("QR code (accès sécurisé au dossier)")],
  ];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] gap-5 items-start mt-6" data-testid="negotiation-step-review">
      <div className="space-y-4 min-w-0">
        {attention > 0 && (
          <div className="rounded-2xl border border-warning/25 bg-warning/[0.06] p-4 flex gap-3" data-testid="negotiation-review-warning">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <div className="text-xs text-warning/90">{attention} {attention > 1 ? tfr("champs nécessitent encore votre attention — vous pouvez tout de même générer la fiche.") : tfr("champ nécessite encore votre attention — vous pouvez tout de même générer la fiche.")}</div>
          </div>
        )}
        <div className="rounded-2xl border border-border bg-surface p-5">
          <h2 className="text-sm font-semibold">{tfr("Options du document")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground/80">Template</label>
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                className="w-full h-11 rounded-lg border border-border bg-background/60 px-3 text-sm" data-testid="negotiation-review-template">
                {readiness.templates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground/80" htmlFor="season-label">Saison</label>
              <Input id="season-label" value={form.season_label} onChange={(e) => setForm((f) => ({ ...f, season_label: e.target.value }))}
                placeholder="ex: 2025/26" className="h-11 border-border bg-background/60" data-testid="negotiation-review-season" />
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {toggles.map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <span className="text-xs">{label}</span>
                <Switch checked={sections[k] !== false} onCheckedChange={(v) => setSections((s) => ({ ...s, [k]: v }))} data-testid={`negotiation-section-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-surface p-5">
          <h2 className="text-sm font-semibold">{tfr("Récapitulatif")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-3 text-sm">
            {[[tfr("Valeur de marché"), form.market_value], [tfr("Prétentions"), form.salary_expectation], [tfr("Fin de contrat"), form.contract_end_date],
              [tfr("Disponibilité"), form.availability], [tfr("Marché préféré"), form.preferred_market],
              [tfr("Stats renseignées"), `${Object.values(form.season_stats || {}).filter((v) => v !== "").length}/${(readiness.stats_fields || []).length}`]]
              .map(([l, v]) => (
                <div key={l}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{l}</div>
                  <div className="font-medium mt-0.5 truncate">{v || "—"}</div>
                </div>
              ))}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" className="h-10 border-border" onClick={onBack}><ChevronLeft className="h-4 w-4 mr-1" /> {tfr("Retour")}</Button>
          <Button onClick={onNext} className="h-10 gap-1.5" data-testid="negotiation-step3-next">{tfr("Passer à la génération")} <ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-surface p-4 min-w-0 xl:sticky xl:top-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">{tfr("Aperçu du template")}</h2>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs border-border" onClick={onExpand}><Maximize2 className="h-3.5 w-3.5" /> Agrandir</Button>
        </div>
        <A4Preview {...previewProps} />
      </div>
    </div>
  );
}

/* ---------- Expanded preview with zoom ---------- */
function ExpandedPreview({ previewProps }) {
  const [zoom, setZoom] = useState(1);
  return (
    <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="negotiation-expanded-preview">
      <DialogHeader className="flex-row items-center justify-between space-y-0 pr-8">
        <DialogTitle className="text-sm">{tfr("Aperçu de la fiche de négociation")}</DialogTitle>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-border" onClick={() => setZoom((z) => Math.max(0.5, z - 0.15))} aria-label={tfr("Zoom arrière")} data-testid="negotiation-zoom-out"><ZoomOut className="h-3.5 w-3.5" /></Button>
          <Button variant="outline" size="sm" className="h-8 px-2 text-xs border-border" onClick={() => setZoom(1)} data-testid="negotiation-zoom-fit">Ajuster</Button>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-border" onClick={() => setZoom((z) => Math.min(2, z + 0.15))} aria-label="Zoom avant" data-testid="negotiation-zoom-in"><ZoomIn className="h-3.5 w-3.5" /></Button>
        </div>
      </DialogHeader>
      <div className="overflow-auto flex-1 bg-black/30 rounded-lg p-4">
        <div style={{ width: `${zoom * 100}%`, maxWidth: zoom <= 1 ? "700px" : undefined }} className="mx-auto transition-[width] duration-150">
          <A4Preview {...previewProps} />
        </div>
      </div>
    </DialogContent>
  );
}

/* ---------- Existing profiles list ---------- */
function LandingList({ profiles, readiness, onDownload, onNew, onUpdateLast }) {
  return (
    <div className="mt-6 space-y-4 max-w-3xl" data-testid="negotiation-landing">
      <div className="rounded-2xl border border-border bg-surface p-5 md:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-semibold">Fiches existantes</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{profiles.length} fiche{profiles.length > 1 ? "s" : ""} — les PDF générés ne sont jamais écrasés (versions conservées).</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="h-10 border-border" onClick={onUpdateLast} data-testid="negotiation-update-last">{tfr("Mettre à jour la dernière fiche")}</Button>
            <Button size="sm" onClick={onNew} className="h-10 gap-1.5" data-testid="negotiation-new"><Plus className="h-4 w-4" /> {tfr("Nouvelle fiche")}</Button>
          </div>
        </div>
        <ul className="mt-4 divide-y divide-border/60">
          {profiles.map((p) => {
            const tpl = (readiness.templates || []).find((t) => t.id === p.template_id);
            return (
              <li key={p.id} className="flex items-center justify-between gap-3 py-3" data-testid={`negotiation-profile-row-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0"><FileText className="h-4 w-4" /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{tpl?.label || "Fiche"}{p.version ? ` · V${p.version}` : ""} · {formatDate(p.created_at)}</div>
                    <div className="text-[11px] text-muted-foreground">{p.status === "generated" ? tfr("PDF généré") : tfr("Brouillon")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={cn("text-[10px] border", p.status === "generated" ? "bg-success/10 text-success border-success/30" : "bg-secondary/50 text-muted-foreground border-border")}>
                    {p.status === "generated" ? tfr("Générée") : tfr("Brouillon")}
                  </Badge>
                  {p.document_id && (
                    <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => onDownload(p.document_id, null)} data-testid={`negotiation-download-${p.id}`}>
                      <Download className="h-3.5 w-3.5" /> PDF
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
